import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Project {
  id: string;
  title: string;
  location: string;
  project_images: { image_url: string; alt_text?: string }[];
  project_category_assignments?: {
    project_categories: { name: string };
  }[];
}

interface CityGroup {
  display_cities: string[];
  region: string;
  county: string;
  primary_city: string;
}

interface RecentProjectsProps {
  cityName?: string;
  serviceSlug?: string;
}

export const RecentProjects = ({ cityName, serviceSlug }: RecentProjectsProps = {}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [cityGroup, setCityGroup] = useState<CityGroup | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<{ [key: string]: boolean }>({});
  const [selectedImages, setSelectedImages] = useState<{ [key: string]: number }>({});
  const [galleryMode, setGalleryMode] = useState<{ [key: string]: boolean }>({});

  // Helper function to check if a location matches the city
  const locationMatchesCity = (location: string | null, cityName: string): boolean => {
    if (!location || !cityName) return false;
    const normalizedLocation = location.toLowerCase();
    const normalizedCity = cityName.toLowerCase().replace(/-/g, ' ');
    return normalizedLocation.includes(normalizedCity);
  };

  // Helper function to get regional projects with smart city selection
  const getCityProjects = (featuredProjects: Project[], cityGroup: CityGroup | null): Project[] => {
    if (!cityGroup || featuredProjects.length === 0) {
      return featuredProjects.slice(0, 6); // Show 6 projects instead of 3
    }

    // Use the first 6 featured projects and intelligently assign cities
    const selectedProjects = featuredProjects.slice(0, 6);
    const regionCities = [cityGroup.primary_city, ...cityGroup.display_cities];
    
    return selectedProjects.map((project) => {
      // If project already has a location that matches any city in the group, keep it
      const hasMatchingLocation = regionCities.some(city => 
        locationMatchesCity(project.location, city)
      );
      
      if (hasMatchingLocation && project.location) {
        return project; // Keep original location
      }
      
      // Otherwise, assign a city from the city group
      const randomCity = regionCities[Math.floor(Math.random() * regionCities.length)];
      return {
        ...project,
        location: `${randomCity}, NY`
      };
    });
  };

  // Fetch city group data based on cityName  
  useEffect(() => {
    const fetchCityGroup = async () => {
      if (!cityName || cityName === "Long Island") return;
      
      try {
        // Find the city group that contains this city in its display_cities array
        const { data: cityGroupData, error } = await supabase
          .from('city_groups')
          .select('display_cities, region, county, primary_city')
          .contains('display_cities', [cityName])
          .maybeSingle();

        if (error) {
          console.log('Error fetching city group:', error);
          return;
        }

        if (cityGroupData) {
          setCityGroup(cityGroupData);
        } else {
          console.log('No city group found for:', cityName);
        }
      } catch (err) {
        console.error('Error fetching city group:', err);
      }
    };

    fetchCityGroup();
  }, [cityName]);

  // Fetch featured projects from database
  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        let featuredProjects: any[] = [];

        // Priority 1: Location-specific projects (if visiting from a specific city)
        if (cityName && featuredProjects.length === 0) {
          const normalizedCity = cityName.toLowerCase().replace(/-/g, ' ');
          
          const { data: locationProjects, error: locationError } = await supabase
            .from('projects')
            .select(`
              id,
              title,
              location,
              project_images (
                image_url,
                alt_text
              ),
              project_category_assignments (
                project_categories (
                  name
                )
              )
            `)
            .eq('status', 'published')
            .ilike('location', `%${normalizedCity}%`)
            .limit(6);

          if (!locationError && locationProjects && locationProjects.length > 0) {
            featuredProjects = locationProjects;
          }
        }

        // Priority 2: Service-specific featured projects
        if (serviceSlug && featuredProjects.length === 0) {
          const { data: serviceData, error: serviceError } = await supabase
            .from('page_featured_projects')
            .select(`
              project_id,
              display_order,
              projects (
                id,
                title,
                location,
                project_images (
                  image_url,
                  alt_text
                ),
                project_category_assignments (
                  project_categories (
                    name
                  )
                )
              )
            `)
            .eq('page_type', 'service')
            .eq('service_slug', serviceSlug)
            .eq('is_active', true)
            .order('display_order');

          if (!serviceError && serviceData && serviceData.length > 0) {
            featuredProjects = serviceData.map(item => item.projects).filter(Boolean);
          }

          // Priority 3: Category-matching projects for the service
          if (featuredProjects.length === 0) {
            const { data: categoryProjects, error: catError } = await supabase
              .from('projects')
              .select(`
                id,
                title,
                location,
                project_images (
                  image_url,
                  alt_text
                ),
                project_category_assignments!inner (
                  project_categories!inner (
                    name,
                    service_categories!inner (
                      services!inner (
                        slug
                      )
                    )
                  )
                )
              `)
              .eq('status', 'published')
              .eq('project_category_assignments.project_categories.service_categories.services.slug', serviceSlug)
              .order('created_at', { ascending: false })
              .limit(6);

            if (!catError && categoryProjects && categoryProjects.length > 0) {
              featuredProjects = categoryProjects;
            }
          }
        }

        // Priority 4: Page-specific featured projects for cities or homepage
        if (featuredProjects.length === 0) {
          let pageQuery = supabase
            .from('page_featured_projects')
            .select(`
              project_id,
              display_order,
              projects (
                id,
                title,
                location,
                project_images (
                  image_url,
                  alt_text
                ),
                project_category_assignments (
                  project_categories (
                    name
                  )
                )
              )
            `)
            .eq('is_active', true);

          // Filter by page type
          if (cityName) {
            pageQuery = pageQuery.eq('page_type', 'city').eq('city_name', cityName);
          } else {
            pageQuery = pageQuery.eq('page_type', 'homepage');
          }

          const { data: pageData, error: pageError } = await pageQuery.order('display_order');

          if (pageData && pageData.length > 0) {
            // Use page-specific projects
            featuredProjects = pageData.map(item => item.projects).filter(Boolean);
          }
        }

        // Priority 5: Fallback to old featured_projects table
        if (featuredProjects.length === 0) {
          const { data: legacyData, error: legacyError } = await supabase
            .from('featured_projects')
            .select(`
              projects (
                id,
                title,
                location,
                project_images (
                  image_url,
                  alt_text
                ),
                project_category_assignments (
                  project_categories (
                    name
                  )
                )
              )
            `)
            .eq('is_active', true)
            .order('display_order');

          if (!legacyError && legacyData) {
            featuredProjects = legacyData.map(item => item.projects).filter(Boolean);
          }
        }

        const cityProjects = getCityProjects(featuredProjects, cityGroup);
        setProjects(cityProjects);
      } catch (error) {
        console.error('Error fetching featured projects:', error);
        // Final fallback: try to get any published projects
        try {
          const { data: allProjects, error: fallbackError } = await supabase
            .from('projects')
            .select(`
              id,
              title,
              location,
              project_images (
                image_url,
                alt_text
              ),
              project_category_assignments (
                project_categories (
                  name
                )
              )
            `)
            .eq('status', 'published')
            .limit(6);

          if (fallbackError) throw fallbackError;
          setProjects(allProjects || []);
        } catch (fallbackError) {
          console.error('Error fetching fallback projects:', fallbackError);
          setProjects([]);
        }
      }
    };

    fetchFeaturedProjects();
  }, [cityName, serviceSlug, cityGroup]);

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-left mb-8">
          <h3 className="text-4xl font-light text-foreground leading-tight">
            Recent Projects
          </h3>
          <p className="text-4xl font-light text-foreground">
            {cityGroup?.region ? `in ${cityGroup.region}` : 'In Your Area'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {projects.length > 0 ? (
            projects.map((project, index) => {
              const currentImageIndex = selectedImages[project.id] || 0;
              const isExpanded = expandedProjects[project.id] || false;
              
              return (
                <div 
                  key={project.id} 
                  className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-border/50"
                >
                  <div 
                    className="aspect-[4/3] overflow-hidden cursor-pointer relative"
                    onClick={() => {
                      if (project.project_images.length > 1) {
                        setExpandedProjects(prev => ({
                          ...prev,
                          [project.id]: !prev[project.id]
                        }));
                      }
                    }}
                  >
                    {project.project_images.length > 0 ? (
                      <img 
                        src={project.project_images[currentImageIndex].image_url}
                        alt={project.project_images[currentImageIndex].alt_text || `${project.title} - Professional roofing project`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                        width="800"
                        height="600"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground">No image available</span>
                      </div>
                    )}

                    {/* Project Info Overlay - Hidden when in gallery mode */}
                    {!galleryMode[project.id] && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                        <h4 className="font-bold text-lg mb-1 text-white">{project.title}</h4>
                        <div className="flex items-center text-sm text-white/90 mb-2">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {project.location}
                        </div>
                        
                        {/* Category Tags */}
                        <div className="flex flex-wrap gap-1">
                          {project.project_category_assignments?.slice(0, 2).map((assignment, tagIndex) => (
                            <span key={tagIndex} className="px-2 py-1 bg-white/20 text-white rounded-full text-xs font-medium backdrop-blur-sm">
                              {assignment.project_categories.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                  </div>
                  
                  {/* Thumbnail Strip - Shows when expanded */}
                  {isExpanded && project.project_images.length > 1 && (
                    <div className="bg-muted/50 p-2 border-t border-border/50 animate-fade-in">
                      <div className="flex space-x-2 overflow-x-auto">
                        {project.project_images.map((image, imgIndex) => (
                          <button
                            key={imgIndex}
                            onClick={() => {
                              setSelectedImages(prev => ({
                                ...prev,
                                [project.id]: imgIndex
                              }));
                              setGalleryMode(prev => ({
                                ...prev,
                                [project.id]: imgIndex !== 0
                              }));
                            }}
                            className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all duration-200 ${
                              imgIndex === currentImageIndex 
                                ? 'border-primary shadow-md' 
                                : 'border-border/50 hover:border-primary/50'
                            }`}
                          >
                            <img
                              src={image.image_url}
                              alt={image.alt_text || `${project.title} thumbnail ${imgIndex + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              width="64"
                              height="48"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  
                  {/* Completion Badge */}
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-green-700 text-white text-xs font-bold rounded-full shadow-lg">
                      ✓ COMPLETED
                    </span>
                  </div>

                  {/* Photo count badge */}
                  {project.project_images.length > 1 && (
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-black/70 text-white text-xs font-medium rounded-full shadow-lg">
                        {project.project_images.length} Photos
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-8 text-muted-foreground">
              <p>No featured projects available.</p>
              <p className="text-sm mt-2">Contact us to see our latest work!</p>
            </div>
          )}
        </div>

      </div>
    </section>
  );
};